import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { MediaQueryService } from '@frontend/vanilla/core';
import { Observable, distinctUntilChanged, map } from 'rxjs';

import { Tracking, WalletItem } from './account-balance.models';
import { AccountBalanceService } from './account-balance.service';

@Component({
    selector: 'ms-wallet-selection-dropdown',
    templateUrl: './wallet-selection-dropdown.component.html',
    styleUrls: ['./wallet-selection-dropdown.component.scss'],
    standalone: false,
})
export class WalletSelectionDropdownComponent {
    @HostBinding('class') className = 'group-selector';
    @HostBinding('class.active') expanded = false;

    @Input() showExtendedInfo?: boolean;
    @Input() trackingPosition: string;
    @Input() inColumn = false;
    @Input() singleWallet = false;
    @Input() bottomOptions = false;
    @Output()
    expandWalletDropDown = new EventEmitter<boolean>();

    @Output()
    openPayPalHelp = new EventEmitter<boolean>();

    payPalHelp = false;
    wasPayPalHelpShown = false;
    preSelectedWallet: WalletItem | undefined;

    top = 0;
    right = 0;
    mediaQuery$: Observable<boolean>;
    mediaQuerySignal: Signal<boolean>;

    constructor(
        private elementRef: ElementRef,
        public accountBalanceService: AccountBalanceService,
        public siteCore: Sitecore,
        private trackingService: TrackingService,
        private mediaQuery: MediaQueryService,
    ) {
        this.mediaQuery$ = this.mediaQuery.observe().pipe(
            map(() => this.mediaQuery.isActive('lt-md')),
            distinctUntilChanged(),
        );
        this.mediaQuerySignal = toSignal(this.mediaQuery$, { requireSync: true });
    }

    @HostListener('document:click')
    clickout(): void {
        if (!this.wasPayPalHelpShown) {
            this.closePayPalHelp();
        }
        this.wasPayPalHelpShown = false;
    }

    showPayPalHelp(walletsCount?: number): void {
        this.payPalHelp = true;
        this.wasPayPalHelpShown = true;
        this.reset();
        if (this.bottomOptions) {
            this.toggled(walletsCount || 0);
        } else {
            this.openPayPalHelp.emit(this.payPalHelp);
        }
    }

    closePayPalHelp(): void {
        this.preSelectedWallet = undefined;
        this.payPalHelp = false;
        this.openPayPalHelp.emit(this.payPalHelp);
    }

    get selectedWallet(): WalletItem | undefined {
        return this.accountBalanceService.selectedWallet;
    }

    private get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    private get parent(): HTMLElement {
        return this.elementRef.nativeElement.offsetParent ?? this.elementRef.nativeElement.parentElement;
    }

    toggled(walletsCount: number): void {
        if (walletsCount <= 1) {
            return;
        }

        if (!this.expanded) {
            this.trackDropdown({
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'Wallet Dropdown - Clicked',
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'User Clicks on Wallet Dropdown',
            });
        }

        const { offsetTop, offsetLeft, offsetHeight, offsetWidth } = this.element;
        this.top = offsetTop + offsetHeight;
        this.right = this.parent.offsetWidth - offsetLeft - offsetWidth;
        this.expanded = !this.expanded;
        this.expandWalletDropDown.emit(this.expanded);
    }

    selectionChanged(wallet?: WalletItem): void {
        if (!wallet) {
            return;
        }

        this.accountBalanceService.selectWallet(wallet.type);
        this.trackDropdown({
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Wallet  - Selected',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `${wallet.type} Balance - Selected`,
        });
        this.reset();
    }

    setPreSelectedWallet(wallet: WalletItem, event: Event): void {
        this.preSelectedWallet = wallet;

        event.preventDefault();
        event.stopPropagation();
    }

    private reset(): void {
        if (!this.expanded) {
            return;
        }
        this.expandWalletDropDown.emit(!this.expanded);
        this.expanded = false;
    }

    private getTrackingSiteSection = (): string => (this.trackingPosition !== Tracking.Betslip ? Tracking.SportsSiteSection : this.trackingPosition);

    private trackDropdown(additionalTrackData: Partial<TrackData>): void {
        const baseTrackData = {
            [trackingConstants.PAGE_SITE_SECTION]: this.getTrackingSiteSection(),
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: Tracking.Wallets,
            [trackingConstants.COMPONENT_LABEL_EVENT]: Tracking.PayPalWallet,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.inColumn ? Tracking.Desktop : Tracking.Mobile,
            [trackingConstants.COMPONENT_URL_CLICKED]: Tracking.NotApplicable,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.trackingPosition,
        };
        const fullTrackData = { ...baseTrackData, ...additionalTrackData };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, fullTrackData);
    }
}
